<template>
  <div :class="{'fixed-height' : fixedHeight}" class="entity-workflow-selector">
    <div
      v-for="workflow in workflows"
      :key="workflow.id"
      :ref="getWorkflowRef(workflow.id)"
      :class="[
        {'selected' : isSelected(workflow)},
        {'mailbox' : entityType === EEntities.MAILBOXES }
      ]"
      class="entity-workflow-selector__item"
      @click="selectWorkflow(workflow)"
    >
      <i v-if="isSelected(workflow)" class="ddi ddi-check-thick" />
      <div class="title-and-desc">
        <p><strong>{{workflow.name}}</strong></p>
        <p class="small">{{workflow.description}}</p>
      </div>
      <div class="attributes">
        <div>
          <status-chicklet
            v-for="status in workflow.listStatuses" 
            :key="status.id" 
            :status="status" 
          />
        </div>
        <div v-if="$permissions.jobsFeatureEnabled && hasJobs(workflow)" class="jobs">
          <chicklet
            v-for="job in workflow.listJobs" 
            :key="job.id" 
            :title="job.name" 
            is-uppercase
            size="tiny"
          >
            <span>{{job.name}}</span>
          </chicklet>
        </div>
      </div>
    </div> 
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */
import { IWorkflowForSelection } from '@/api/types/response.types'
import { EEntities } from '@/types'

/* API */

export default defineComponent({
  name: 'EntityWorkflowSelector',

  props: {
    workflows: {
      type: Array as () => IWorkflowForSelection[],
      required: true,
    },
    modelValue: {
      type: Number,
      default: null,
    },
    defaultValue: {
      type: Number,
      default: null,
    },
    fixedHeight: {
      type: Boolean,
      default: true,
    },
    entityType: {
      type: String as () => EEntities,
      required: true,
    },
  },

  emits: ['update:modelValue'],
  data() {
    return {
      EEntities,
    }
  },
  computed: {
    internalValue: {
      get(): number {
        return this.modelValue
      },
      set(value: number) {
        this.$emit('update:modelValue', value)
      },
    },
  },
  watch: {
    defaultValue: {
      immediate: true,
      handler(val) {
        if (val !== null) {
          this.internalValue = val
          this.$nextTick(() => {
            // Refs inside v-for are arrays; there are none yet when this first runs on creation
            const refs = this.$refs[this.getWorkflowRef(val)] as HTMLElement[] | undefined
            const item = refs?.[0]
            if (item) {
              // Scroll just the list: scrollIntoView would scroll the page too, tucking the form under the header
              const list = this.$el as HTMLElement
              list.scrollTop += item.getBoundingClientRect().top - list.getBoundingClientRect().top
            }
          })
        }
      },
    },
  },

  methods: {
    getWorkflowRef(workflowID: number): string {
      return `workflow-${workflowID}`
    },
    isSelected(workflow: IWorkflowForSelection): boolean {
      return this.internalValue === workflow.id
    },
    selectWorkflow(workflow: IWorkflowForSelection) {
      this.internalValue = workflow.id
    },
    hasJobs(workflow: IWorkflowForSelection): boolean {
      return workflow.listJobs.length > 0
    },
  },
})
</script>

<style lang="scss">
.entity-workflow-selector {
  margin-bottom: 20px;
  padding-top: 5px;
  box-shadow: inset 0 2px 5px $color--gray-30;
  border: var(--border-solid-extra-light);

  &.fixed-height {
    max-height: 40vh;
    overflow-y: scroll;
  }

  &__item {
    position: relative;
    display: flex;
    flex-direction: column;
    @include flex-row-horizontal-spacing(10px);
    padding: 16px 0 16px 40px;
    border: var(--border-solid-extra-light);
    margin: 5px 10px;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      background-color: var(--color-background-blue);
    }

    &.selected {
      background-color: var(--color-background-blue-15);
      border-color: $color--blue;
    }

    .ddi-check-thick {
      position: absolute;
      left: 6px;
      top: 28px;
      transform: translateY(-50%);
      color: $color--blue-60;
      font-size: 28px;
    }

    &.mailbox {
      &:hover {
        background-color: var(--color-background-green);
      }

      &.selected {
        background-color: var(--color-background-green-15);
        border-color: $color--green;
      }

      .ddi-check-thick {
        color: $color--green-60;
      }
    }

    .attributes {
      display: flex;
      flex-direction: column;
      margin-top: 10px;

      > div {
        display: flex;
        @include flex-row-horizontal-spacing(4px);
        @include flex-row-vertical-spacing-on-wrap(4px);

        &.jobs {
          margin-top: 10px;
        }

        @include media-breakpoint-down(md) {
          width: 100%;
        }
      }
    }

    @include media-breakpoint-down(md) {
      flex-direction: column;
      @include flex-column-vertical-spacing(8px);
    }
  }
}

html[data-theme='dark'] {
  .entity-workflow-selector {
    box-shadow: inset 0 2px 5px #000;
  }
}
</style>
